// components/ProductSelector.jsx - Product Selection with Favorites (Multi-Select)
import React, { useState, useMemo } from 'react'
import { Search, Star, Plus, Check, ChevronDown, ChevronUp, X } from 'lucide-react'
import * as api from '../services/api'

export default function ProductSelector({
  products,
  selectedProduct,  // Keep for backward compatibility with single-select mode
  selectedProducts, // New: array for multi-select mode
  method,
  onSelect,
  onRemove,  // New: callback to remove a product from selection
  onAddNew,
  shortCode,
  multiSelect = false  // New: enable multi-select mode
}) {
  // Use multi-select or single-select mode
  const selected = multiSelect ? selectedProducts : (selectedProduct ? [selectedProduct] : [])
  const [searchTerm, setSearchTerm] = useState('')
  const [showAllProducts, setShowAllProducts] = useState(false)
  const [togglingFavorite, setTogglingFavorite] = useState(null)
  const [optimisticFavorites, setOptimisticFavorites] = useState({}) // Track optimistic updates
  
  // Helper to check if product is favorited (considering optimistic updates)
  const isFavorited = (product) => {
    const key = `${product.name}-${method}`
    // If we have an optimistic override, use it
    if (key in optimisticFavorites) {
      return optimisticFavorites[key]
    }
    // Otherwise use the actual data
    return product.favoriteMethods?.includes(method)
  }

  // Separate favorites and all products
  const { favorites, otherProducts } = useMemo(() => {
    const favs = products.filter(p => isFavorited(p))
    const others = products.filter(p => !isFavorited(p))
    return { favorites: favs, otherProducts: others }
  }, [products, method, optimisticFavorites])
  
  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return { favorites, otherProducts }
    
    const term = searchTerm.toLowerCase()
    const filterFn = p => 
      p.name.toLowerCase().includes(term) ||
      p.epaNumber?.toLowerCase().includes(term) ||
      p.activeIngredient?.toLowerCase().includes(term)
    
    return {
      favorites: favorites.filter(filterFn),
      otherProducts: otherProducts.filter(filterFn)
    }
  }, [favorites, otherProducts, searchTerm])
  
  // Toggle favorite status with optimistic update
  const toggleFavorite = async (e, product) => {
    e.stopPropagation()

    if (togglingFavorite) return

    const key = `${product.name}-${method}`
    const currentlyFavorited = isFavorited(product)
    const newFavoriteState = !currentlyFavorited

    setTogglingFavorite(product.name)

    // Optimistic update - immediately show the new state
    setOptimisticFavorites(prev => ({
      ...prev,
      [key]: newFavoriteState
    }))

    try {
      await api.toggleFavorite(shortCode, product.name, method)
      // Success - keep the optimistic state (it will be overwritten on next data refresh)
    } catch (err) {
      console.error('Failed to toggle favorite:', err)
      // Revert optimistic update on error
      setOptimisticFavorites(prev => ({
        ...prev,
        [key]: currentlyFavorited
      }))
    } finally {
      setTogglingFavorite(null)
    }
  }
  
  const ProductCard = ({ product, isFavorite }) => {
    const isSelected = selected.includes(product.name)

    return (
      <button
        onClick={() => {
          if (multiSelect && isSelected) {
            // In multi-select, clicking selected product removes it
            onRemove(product.name)
          } else {
            // Single select or selecting new product
            onSelect(product.name)
          }
        }}
        className={`product-card w-full text-left ${isSelected ? 'selected' : ''}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 truncate">{product.name}</h4>
            {product.epaNumber && (
              <p className="text-xs text-gray-500 mt-0.5">EPA #{product.epaNumber}</p>
            )}
            {product.activeIngredient && (
              <p className="text-xs text-gray-500">
                {product.activeIngredient}
                {product.percentActiveIngredient && ` (${product.percentActiveIngredient}%)`}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 ml-2">
            <button
              onClick={(e) => toggleFavorite(e, product)}
              className={`p-1.5 rounded-full transition-colors ${
                isFavorite
                  ? 'text-yellow-500 hover:bg-yellow-50'
                  : 'text-gray-300 hover:text-yellow-400 hover:bg-gray-50'
              }`}
              disabled={togglingFavorite === product.name}
            >
              <Star className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
            </button>

            {isSelected && (
              <Check className="w-5 h-5 text-arbor-500" />
            )}
          </div>
        </div>
      </button>
    )
  }
  
  return (
    <div className="p-4">
      {/* Selected products chips (multi-select mode) */}
      {multiSelect && selected.length > 0 && (
        <div className="mb-4 p-3 bg-arbor-50 border border-arbor-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Selected Products ({selected.length})
            </span>
            {selected.length > 1 && (
              <button
                onClick={() => {
                  selected.forEach(productName => onRemove(productName))
                }}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Clear All
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {selected.map((productName) => (
              <div
                key={productName}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-arbor-300 rounded-full text-sm"
              >
                <span>{productName}</span>
                <button
                  onClick={() => onRemove(productName)}
                  className="text-gray-400 hover:text-red-600 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="form-input pl-10"
        />
      </div>
      
      {/* Favorites section */}
      {filteredProducts.favorites.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-500 fill-current" />
            Favorites for this method
          </h3>
          <div className="space-y-2">
            {filteredProducts.favorites.map(product => (
              <ProductCard key={product.name} product={product} isFavorite={true} />
            ))}
          </div>
        </div>
      )}
      
      {/* All products section */}
      <div>
        {filteredProducts.favorites.length > 0 && (
          <button
            onClick={() => setShowAllProducts(!showAllProducts)}
            className="flex items-center gap-2 text-sm text-gray-600 mb-2"
          >
            {showAllProducts ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Hide other products
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show all products ({filteredProducts.otherProducts.length})
              </>
            )}
          </button>
        )}
        
        {(showAllProducts || filteredProducts.favorites.length === 0) && (
          <div className="space-y-2">
            {filteredProducts.otherProducts.map(product => (
              <ProductCard key={product.name} product={product} isFavorite={false} />
            ))}
            
            {filteredProducts.otherProducts.length === 0 && filteredProducts.favorites.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>No products found</p>
                {searchTerm && (
                  <p className="text-sm mt-1">Try a different search term</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Add new product button */}
      <button
        onClick={onAddNew}
        className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg
                   text-gray-600 flex items-center justify-center gap-2
                   hover:border-arbor-400 hover:text-arbor-600 transition-colors"
      >
        <Plus className="w-5 h-5" />
        Add New Product
      </button>
    </div>
  )
}
